'use client'

import {
  Button,
  Column,
  Grid,
  Layer,
  Link,
  Tag,
  Tile,
} from '@carbon/react'
import {
  Catalog,
  Certificate,
  Code,
  DataBase,
  Email,
  FolderOpen,
  Globe,
  Launch,
  ServerProxy,
  Terminal,
  View,
  WatsonHealthAiStatus,
} from '@carbon/react/icons'
import type { KmerLocale } from '@kmerhosting/i18n'
import { useEffect, useRef, useState } from 'react'
import { DatacenterLocations } from '@/components/datacenter-locations'
import type { HomeCopy } from '@/lib/home-i18n'

type CatalogStatus = 'available' | 'coming-soon' | 'out-of-stock'

type LandingItem = {
  slug: string
  name: string
  shortDescription: string
  status: CatalogStatus
}

type CloudLandingClientProps = {
  services: LandingItem[]
  products: LandingItem[]
  roadmapCount: number
  locale: KmerLocale
  copy: HomeCopy
}

const serviceIcons = {
  'domain-registration': Globe,
  'email-hosting': Email,
  'database-hosting': DataBase,
  'ssl-certificates': Certificate,
  'kvm-vps-hosting': ServerProxy,
  'reseller-hosting-directadmin': ServerProxy,
  'bare-metal-server': Terminal,
} as const

const serviceLogos: Partial<Record<string, { src: string; alt: string }>> = {
  'lxc-vps-hosting': { src: '/service-logos/lxc.svg', alt: 'Linux Containers' },
  'shared-hosting-directadmin': { src: '/platform-logos/directadmin.svg', alt: 'DirectAdmin' },
  'shared-hosting-cpanel': { src: '/platform-logos/cpanel.svg', alt: 'cPanel' },
  'reseller-hosting-directadmin': { src: '/platform-logos/directadmin.svg', alt: 'DirectAdmin' },
  'free-hosting': { src: '/brand/logo-blue-transparent.png', alt: 'KmerHosting' },
  'php-hosting': { src: '/service-logos/php.svg', alt: 'PHP' },
  'wordpress-hosting': { src: '/service-logos/wordpress.svg', alt: 'WordPress' },
  'nodejs-hosting': { src: '/service-logos/nodejs.svg', alt: 'Node.js' },
  'python-hosting': { src: '/service-logos/python.svg', alt: 'Python' },
  'laravel-hosting': { src: '/service-logos/laravel.svg', alt: 'Laravel' },
}

const productIcons = {
  convertsuite: Code,
  wfilemanager: FolderOpen,
  webcpl: Code,
  'kmerhosting-drive': FolderOpen,
  'kmerhosting-ai-agent': WatsonHealthAiStatus,
} as const

const osImages: [string, string][] = [
  ['AlmaLinux', '/os-logos/almalinux.svg'],
  ['CentOS', '/os-logos/centos.svg'],
  ['Debian', '/os-logos/debian.svg'],
  ['FreeBSD', '/os-logos/freebsd.svg'],
  ['openSUSE', '/os-logos/opensuse.svg'],
  ['Rocky Linux', '/os-logos/rockylinux.svg'],
  ['Ubuntu', '/os-logos/ubuntu.svg'],
  ['cPanel', '/platform-logos/cpanel.svg'],
  ['DirectAdmin', '/platform-logos/directadmin.svg'],
]

function localizedHref(href: string, locale: KmerLocale) {
  if (!href.startsWith('/') || href.startsWith('//')) return href
  const hashIndex = href.indexOf('#')
  const hash = hashIndex >= 0 ? href.slice(hashIndex) : ''
  const path = hashIndex >= 0 ? href.slice(0, hashIndex) : href
  if (locale === 'en') return `${path || '/'}${hash}`
  return `/${locale}${path === '/' ? '/' : path}${hash}`
}

function isUnavailable(status: CatalogStatus) {
  return status === 'coming-soon' || status === 'out-of-stock'
}

function statusLabel(status: CatalogStatus, copy: HomeCopy) {
  if (status === 'coming-soon') return copy.comingSoon
  if (status === 'out-of-stock') return copy.outOfStock
  return null
}

export function CloudLandingClient({ services, products, roadmapCount, locale, copy }: CloudLandingClientProps) {
  const [heroPhraseIndex, setHeroPhraseIndex] = useState(0)
  const serviceCarouselRef = useRef<HTMLDivElement>(null)
  const serviceCarouselHoverPausedRef = useRef(false)
  const serviceCarouselFocusPausedRef = useRef(false)

  useEffect(() => {
    if (copy.heroRotating.length < 2) return
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    if (reducedMotion.matches) return

    const timer = window.setInterval(() => {
      setHeroPhraseIndex((current) => (current + 1) % copy.heroRotating.length)
    }, 3200)

    return () => window.clearInterval(timer)
  }, [copy.heroRotating.length])

  useEffect(() => {
    const carousel = serviceCarouselRef.current
    if (!carousel || services.length === 0) return

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    if (reducedMotion.matches) return

    let frame = 0
    let previousTime = performance.now()
    let position = carousel.scrollLeft
    let loopDistance = 0

    const measureLoopDistance = () => {
      const first = carousel.children[0] as HTMLElement | undefined
      const duplicateFirst = carousel.children[services.length] as HTMLElement | undefined
      loopDistance = first && duplicateFirst
        ? duplicateFirst.offsetLeft - first.offsetLeft
        : 0
    }

    measureLoopDistance()
    const resizeObserver = new ResizeObserver(measureLoopDistance)
    resizeObserver.observe(carousel)

    const animate = (time: number) => {
      const elapsed = Math.min(time - previousTime, 40)
      previousTime = time

      const paused = serviceCarouselHoverPausedRef.current || serviceCarouselFocusPausedRef.current

      if (paused) {
        // Keep the animation accumulator aligned with user-driven scrollbar/touch movement.
        position = carousel.scrollLeft
      } else if (loopDistance > 0 && carousel.scrollWidth > carousel.clientWidth) {
        // Keep sub-pixel distance in our own accumulator. Some browsers round scrollLeft,
        // which otherwise turns tiny per-frame increments into visible intermittent jumps.
        position += elapsed * 0.06

        if (position >= loopDistance) {
          position -= loopDistance
        }

        carousel.scrollLeft = position
      }

      frame = window.requestAnimationFrame(animate)
    }

    frame = window.requestAnimationFrame(animate)

    return () => {
      window.cancelAnimationFrame(frame)
      resizeObserver.disconnect()
    }
  }, [services.length])

  const carouselServices = [...services, ...services]

  const trustItems = [
    { value: 'DirectAdmin', label: copy.directAdminHosting },
    { value: 'cPanel', label: copy.sharedHosting },
    { value: 'LXC VPS', label: copy.linuxContainers },
    { value: 'Domains & Email', label: copy.dedicatedPortals },
  ]

  return (
    <>
      <section className="cloud-hero" aria-labelledby="hero-title">
        <Grid>
          <Column sm={4} md={5} lg={9}>
            <h1
              id="hero-title"
              className="cloud-hero__title"
              aria-label={`${copy.heroLead} ${copy.heroRotating[0]}`}
            >
              <span className="cloud-hero__accent">{copy.heroLead}</span>
              <span className="cloud-hero__rotator" aria-hidden="true">
                {copy.heroRotating.map((phrase, index) => (
                  <span
                    key={phrase}
                    className={`cloud-hero__rotating-text${index === heroPhraseIndex ? ' cloud-hero__rotating-text--active' : ''}`}
                  >
                    {phrase}
                  </span>
                ))}
              </span>
            </h1>
            <p className="cloud-hero__subtitle">{copy.heroSubtitle}</p>
            <div className="hero__actions">
              <Button href={localizedHref('/services', locale)} renderIcon={Catalog}>{copy.exploreServices}</Button>
              <Button href={localizedHref('/pricing', locale)} kind="tertiary" renderIcon={Launch}>{copy.viewPricing}</Button>
            </div>
          </Column>
          <Column sm={4} md={3} lg={7}>
            <Layer>
              <Tile className="cloud-hero__platform">
                <div className="cloud-hero__os-grid" aria-label={copy.osAria}>
                  {osImages.map(([name, src]) => (
                    <div key={name} className="cloud-hero__os-item">
                      <img src={src} alt="" width="32" height="32" loading="lazy" decoding="async" />
                      <span>{name}</span>
                    </div>
                  ))}
                </div>
              </Tile>
            </Layer>
          </Column>
        </Grid>
      </section>

      <section className="trust-strip" aria-label={copy.hostingOptions}>
        <Grid>
          {trustItems.map(({ value, label }) => (
            <Column key={`${value}-${label}`} sm={2} md={2} lg={4} className="trust-strip__item">
              <strong>{value}</strong>
              <span>{label}</span>
            </Column>
          ))}
        </Grid>
      </section>

      <DatacenterLocations copy={copy} />

      <section id="services" className="section capabilities" aria-labelledby="services-title">
        <Grid>
          <Column sm={4} md={4} lg={5}>
            <p className="section-label">{copy.services}</p>
            <h2 id="services-title" className="section__title">{copy.servicesTitle}</h2>
            <p className="section__subtitle">{copy.servicesSubtitle}</p>
          </Column>
          <Column sm={4} md={4} lg={{ span: 10, offset: 1 }}>
            <Layer>
              <div
                ref={serviceCarouselRef}
                className="service-carousel"
                aria-label={copy.servicesTitle}
                onPointerEnter={() => { serviceCarouselHoverPausedRef.current = true }}
                onPointerLeave={() => { serviceCarouselHoverPausedRef.current = false }}
                onFocusCapture={() => { serviceCarouselFocusPausedRef.current = true }}
                onBlurCapture={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                    serviceCarouselFocusPausedRef.current = false
                  }
                }}
              >
                {carouselServices.map(({ slug, name, shortDescription, status }, index) => {
                  const Icon = serviceIcons[slug as keyof typeof serviceIcons] ?? ServerProxy
                  const logo = serviceLogos[slug]
                  const label = statusLabel(status, copy)
                  const duplicate = index >= services.length

                  return (
                    <Link
                      key={`${slug}-${duplicate ? 'duplicate' : 'primary'}`}
                      href={localizedHref(`/services/${slug}`, locale)}
                      className="service-card-link service-carousel__item"
                      aria-hidden={duplicate || undefined}
                      tabIndex={duplicate ? -1 : undefined}
                    >
                      <Tile className="service-tile">
                        <div className="service-tile__visual">
                          {logo ? (
                            <img className="service-tile__logo" src={logo.src} alt={duplicate ? '' : logo.alt} loading="lazy" decoding="async" />
                          ) : (
                            <Icon size={40} aria-hidden="true" />
                          )}
                        </div>
                        <div className="service-tile__content">
                          {isUnavailable(status) ? <Tag type={status === 'out-of-stock' ? 'red' : 'blue'} size="sm">{label}</Tag> : null}
                          <h3>{name}</h3>
                          <p>{shortDescription}</p>
                        </div>
                      </Tile>
                    </Link>
                  )
                })}
              </div>
            </Layer>
          </Column>
        </Grid>
      </section>

      <section id="products" className="section products-section" aria-labelledby="products-title">
        <Grid>
          <Column sm={4} md={4} lg={5}>
            <p className="section-label">{copy.products}</p>
            <h2 id="products-title" className="section__title">{copy.productsTitle}</h2>
            <p className="section__subtitle">{copy.productsSubtitle}</p>
          </Column>
          <Column sm={4} md={4} lg={{ span: 10, offset: 1 }}>
            <div className="product-showcase" aria-label={copy.availableProducts}>
              {products.map(({ slug, name, shortDescription, status }) => {
                const Icon = productIcons[slug as keyof typeof productIcons] ?? Code
                const label = statusLabel(status, copy)

                return (
                  <Link
                    key={slug}
                    href={localizedHref(`/products/${slug}`, locale)}
                    className="product-showcase__link"
                  >
                    <Tile className="product-showcase__card">
                      <div className="product-showcase__visual">
                        <div className="product-showcase__icon" aria-hidden="true">
                          <Icon size={32} />
                        </div>
                        {isUnavailable(status) ? (
                          <Tag type={status === 'out-of-stock' ? 'red' : 'blue'} size="sm">{label}</Tag>
                        ) : null}
                      </div>

                      <div className="product-showcase__content">
                        <h3>{name}</h3>
                        <p>{shortDescription}</p>
                      </div>

                      <span className="product-showcase__action">{copy.details}</span>
                    </Tile>
                  </Link>
                )
              })}
            </div>
          </Column>
        </Grid>
      </section>

      {roadmapCount > 0 ? (
        <section className="section" aria-labelledby="roadmap-preview-title">
          <Grid>
            <Column sm={4} md={4} lg={5}>
              <p className="section-label">{copy.roadmap}</p>
              <h2 id="roadmap-preview-title" className="section__title">{copy.roadmapTitle}</h2>
              <p className="section__subtitle">{copy.roadmapSubtitle}</p>
            </Column>
            <Column sm={4} md={4} lg={{ span: 10, offset: 1 }}>
              <Button href={localizedHref('/roadmap', locale)} kind="tertiary" renderIcon={View}>{copy.viewRoadmap}</Button>
            </Column>
          </Grid>
        </section>
      ) : null}
    </>
  )
}
